import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { User as FirebaseUser, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import AirtableService from '../services/airtable';
import { AirtableCustomer, CustomerUser } from '../types/airtable';

interface CustomerContextValue {
  // Firebase user
  firebaseUser: FirebaseUser | null;
  // Airtable customer data
  customer: AirtableCustomer | null;
  // Combined user object for convenience
  user: CustomerUser | null;
  // Loading states
  loading: boolean;
  customerLoading: boolean;
  // Auth state
  isAuthenticated: boolean;
  // Error handling
  error: string | null;
  // Actions
  logout: () => Promise<void>;
  refreshCustomerData: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [customer, setCustomer] = useState<AirtableCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user: FirebaseUser | null) => {
        setFirebaseUser(user);
        setLoading(false);

        if (user?.email) {
          // Fetch Airtable customer data after Firebase auth
          await fetchCustomerData(user.email);
        } else {
          setCustomer(null);
        }
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const fetchCustomerData = async (email: string): Promise<void> => {
    setCustomerLoading(true);
    setError(null);
    try {
      const customerData = await AirtableService.loginByEmail(email);
      setCustomer(customerData);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch customer data');
      // Don't set customer to null on error - keep existing data if any
    } finally {
      setCustomerLoading(false);
    }
  };

  const refreshCustomerData = async (): Promise<void> => {
    if (firebaseUser?.email) {
      await fetchCustomerData(firebaseUser.email);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setFirebaseUser(null);
      setCustomer(null);
      setError(null);
      // Also clear localStorage (for any cached customer data)
      localStorage.removeItem('enpal_user');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to logout');
    }
  };

  // Create combined user object
  const user: CustomerUser | null = firebaseUser
    ? {
        firebaseUid: firebaseUser.uid,
        email: firebaseUser.email,
        airtableId: customer?.id,
        uid: customer?.uid,
        nome: customer?.nome,
        cognome: customer?.cognome,
        telefono: customer?.telefono,
        indirizzo: customer?.indirizzo,
        installStatus: customer?.installStatus,
        tipoProdotto: customer?.tipoProdotto,
        projectManager: customer?.projectManager,
        buildabilityOutcome: customer?.buildabilityOutcome,
        ipaAppointment: customer?.ipaAppointment,
      }
    : null;

  const value: CustomerContextValue = {
    firebaseUser,
    customer,
    user,
    loading,
    customerLoading,
    isAuthenticated: !!firebaseUser,
    error,
    logout,
    refreshCustomerData,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = (): CustomerContextValue => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
};

export default CustomerContext;
